import React, { RefCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import GenericJsonBuilder from "@/components/general/genericJsonBuilder";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import {
  patternRule,
  requiredRule,
} from "@/components/tables/fields/fieldRules";
import {
  parseJsonBuilderValue,
  shouldAcceptJsonBuilderChange,
} from "@/components/tables/fields/jsonBuilderValue";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";

export interface CrudJsonBuilderFieldProps {
  field: Pick<
    CrudModalField,
    | "id"
    | "header"
    | "required"
    | "errorMessage"
    | "rule"
    | "errorMessageValidation"
    | "hideField"
    | "jsonBuilderProps"
  >;
  modalMode?: string;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: unknown) => void;
}

const CrudJsonBuilderField = ({
  field,
  modalMode,
  validationTargetRef,
  onChange,
}: CrudJsonBuilderFieldProps) => {
  const name = field.id as string;
  const { t } = useTranslation();
  const { control } = useFormContext();
  const formValue = useWatch({ control, name });
  const parsedJson = useMemo(
    () => parseJsonBuilderValue(formValue),
    [formValue]
  );

  if (modalMode === "edit" && formValue == null) return null;
  if (field.hideField) return null;

  return (
    <CrudFieldController
      name={name}
      control={control}
      defaultValue={parsedJson}
      rules={{
        ...requiredRule(field.required, field.errorMessage),
        ...patternRule(field, t),
      }}
      validationTargetRef={validationTargetRef}
      groupInputWithError
      render={({ field: { onChange: setFormValue } }) => {
        if (!((modalMode === "edit" && parsedJson) || modalMode === "insert")) {
          return null; 
        }
        return (
          <GenericJsonBuilder
            label={field.header}
            initialData={parsedJson}
            fieldOptions={field.jsonBuilderProps?.fieldOptions || []}
            renderAs={field.jsonBuilderProps?.renderAs || "form"}
            onChange={(next: any) => {
              if (!shouldAcceptJsonBuilderChange(next, parsedJson, modalMode)) {
                return;
              }
              setFormValue(next);
              onChange?.(next);
            }}
            {...field.jsonBuilderProps}
          />
        );
      }}
    />
  );
};

export default CrudJsonBuilderField;
